import { useContextMenu } from 'mantine-contextmenu';
import { MouseEvent, RefObject, useEffect, useMemo, useState } from 'react';
import {
    MiniMap,
    Node,
    ReactFlow,
    ReactFlowProvider,
    useEdgesState,
    useNodesInitialized,
    useNodesState,
    useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { VersionGraphPanePanel } from './panel/VersionGraphPanePanel';
import {
    maxVariantedVersionGraphNodeHeight,
    VariantedVersionGraphNodeType,
    variantedVersionGraphNodeWidth,
} from './types/VariantedVersionGraphNodeType';
import { VersionGraphEdgeType } from './types/VersionGraphEdgeType';
import { versionGraphNodeHeight, VersionGraphNodeType, versionGraphNodeWidth } from './types/VersionGraphNodeType';
import { VersionGraphEdge, VersionGraphNode } from './VersionGraph';

const nodeTypes = {
    variantedVersionGraphNode: VariantedVersionGraphNodeType,
    versionGraphNodeType: VersionGraphNodeType,
};
const edgeTypes = { 'version-graph-edge': VersionGraphEdgeType };

const VersionGraphContentFlow = ({
    initialNodes,
    initialEdges,
    getNodeContextMenuItems,
    onNodeClick,
    divRef,
}: {
    initialNodes: VersionGraphNode[];
    initialEdges: VersionGraphEdge[];
    getNodeContextMenuItems?: (node: Node) => { onClick: () => void; title: string; key: string; disabled?: boolean }[];
    onNodeClick?: (nodeId: string, nodeLabel: string) => any;
    divRef: RefObject<HTMLDivElement | null>;
}) => {
    const [nodes, setNodes] = useNodesState(initialNodes);
    const [edges, setEdges] = useEdgesState(initialEdges);
    const [showMiniMap, setShowMiniMap] = useState(false);

    const { getViewport, setViewport, fitView } = useReactFlow();
    const nodesInitialized = useNodesInitialized();
    const { showContextMenu } = useContextMenu();

    const calculatePositionByNode = (node: VersionGraphNode) => {
        if (divRef.current) {
            const height = divRef.current.clientHeight;
            const nodeHeight =
                node.type === 'versionGraphNodeType' ? versionGraphNodeHeight : maxVariantedVersionGraphNodeHeight;
            const nodeWidth =
                node.type === 'versionGraphNodeType' ? versionGraphNodeWidth : variantedVersionGraphNodeWidth;
            const x = -node.position.x + divRef.current.clientWidth / 2 - nodeWidth / 2;

            if (node.data.hasParent) {
                if (node.data.hasChild) {
                    return { x, y: -node.position.y + height / 2 - nodeHeight / 2 };
                } else {
                    return { x, y: -node.position.y + 20 };
                }
            } else {
                if (node.data.hasChild) {
                    return { x, y: -node.position.y + height - nodeHeight - 20 };
                } else {
                    return { x, y: -node.position.y + height / 2 - nodeHeight / 2 };
                }
            }
        }

        return { x: -node.position.x + 20, y: -node.position.y + 20 };
    };

    const updateSelectedNode = (selectedNodeId: string) => {
        setNodes((prevState) =>
            prevState.map((node) =>
                node.id === selectedNodeId ?
                    {
                        ...node,
                        selected: true,
                    }
                :   {
                        ...node,
                        selected: false,
                    }
            )
        );
    };

    const hasActiveNode = useMemo(() => {
        return !!initialNodes.find((it) => it.data.isActive);
    }, [nodesInitialized]);

    useEffect(() => {
        setEdges(initialEdges);
        setNodes(initialNodes);
    }, [initialNodes, initialEdges]);

    useEffect(() => {
        setViewportToActiveNode();
    }, [nodes[0].type]);

    const setViewportToActiveNode = () => {
        const activeNode = initialNodes.find((it) => it.data.isActive);
        if (activeNode) {
            const { x, y } = calculatePositionByNode(activeNode);
            const viewport = { x, y, zoom: 1 };
            const activeViewport = getViewport();
            if (viewport.x !== activeViewport.x || viewport.y !== activeViewport.y) {
                setViewport(viewport, { duration: 0 });
            }
        } else {
            fitView();
        }
    };

    return (
        <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onNodeClick={(_, selectedNode: Node) => {
                updateSelectedNode(selectedNode.id);
                if (onNodeClick) {
                    onNodeClick(selectedNode.id, selectedNode.data?.label);
                }
            }}
            onNodeContextMenu={(event: MouseEvent, node: Node) => {
                if (getNodeContextMenuItems) {
                    showContextMenu(getNodeContextMenuItems(node))(event);
                }
            }}
            onInit={() => {
                setViewportToActiveNode();
            }}
            proOptions={{ hideAttribution: true }}
        >
            <VersionGraphPanePanel
                fitView={fitView}
                setViewportToActiveNode={setViewportToActiveNode}
                showMiniMap={showMiniMap}
                setShowMiniMap={setShowMiniMap}
                disableSetViewportToActiveNode={!hasActiveNode}
            />
            {showMiniMap && <MiniMap pannable maskColor={'#E9ECEF99'} nodeColor={'#ADB5BD'} />}
        </ReactFlow>
    );
};

export const VersionGraphContent = ({
    initialNodes,
    initialEdges,
    getNodeContextMenuItems,
    onNodeClick,
    divRef,
}: {
    initialNodes: VersionGraphNode[];
    initialEdges: VersionGraphEdge[];
    getNodeContextMenuItems?: (node: Node) => { onClick: () => void; title: string; key: string; disabled?: boolean }[];
    onNodeClick?: (nodeId: string, nodeLabel: string) => any;
    divRef: RefObject<HTMLDivElement | null>;
}) => {
    return (
        <ReactFlowProvider>
            <VersionGraphContentFlow
                initialNodes={initialNodes}
                initialEdges={initialEdges}
                onNodeClick={onNodeClick}
                getNodeContextMenuItems={getNodeContextMenuItems}
                divRef={divRef}
            />
        </ReactFlowProvider>
    );
};
